import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatLocalDateTime } from '../../utils/dateUtils';
import {
  Snackbar,
  Alert,
  Tooltip,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  Divider,
  Alert as MuiAlert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SensorsIcon from '@mui/icons-material/Sensors';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import NotesIcon from '@mui/icons-material/Notes';
import MapIcon from '@mui/icons-material/Map';
import apiClient from '../../services/apiService';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { t } from 'i18next';

// Helper function to format error messages
const formatErrorMessage = (error: any): string => {
  // Handle FastAPI validation errors - array of objects with loc, msg, type
  if (error?.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    return error.response.data.detail
      .map((err: any) => err.msg || JSON.stringify(err))
      .join('. ');
  }
  
  // Handle FastAPI validation errors - object with loc, msg, type
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'object' && 'msg' in error.response.data.detail) {
    return error.response.data.detail.msg;
  }
  
  // Handle string error details
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'string') {
    return error.response.data.detail;
  }
  
  // Handle generic error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback
  return t('errors.genericError') || 'An unexpected error occurred';
};

// Define interfaces
interface AlertData {
  id: number;
  event_type: string;
  event_data?: Record<string, any>;
  sensor_id: number;
  sensor_name: string;
  sensor_serial_number?: string;
  sensor_imei?: string;
  sensor_location_description?: string;
  sensor_location?: {
    type: string;
    coordinates: [number, number];
  };
  alert_level?: number;
  created_at: string;
  is_acknowledged: boolean;
  acknowledged_at?: string;
  acknowledged_by?: number;
  acknowledger_name?: string;
  notes?: string;
}

const AlertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth(); // Keeping the auth context for future use
  const [alert, setAlert] = useState<AlertData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgeDialogOpen, setAcknowledgeDialogOpen] = useState<boolean>(false);
  const [deleteAlertDialogOpen, setDeleteAlertDialogOpen] = useState<boolean>(false);
  const [deleteAlertStatus, setDeleteAlertStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Fetch alert data
  const fetchAlert = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/alerts/${id}`);
      setAlert(response.data);
      
    } catch (err: any) {
      console.error('Error fetching alert data:', err);
      setError(
        formatErrorMessage(err) || 
        t('alertDetail.errors.failedToLoad')
      );
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  // Load data on component mount
  useEffect(() => {
    if (id) {
      fetchAlert();
    }
  }, [id, fetchAlert]);

  // Handle back button click
  const handleBack = () => {
    navigate('/alerts', { replace: true });
  };

  // Handle view sensor click
  const handleViewSensor = () => {
    if (alert?.sensor_id) {
      navigate(`/sensors/${alert.sensor_id}`);
    }
  };

  // Handle view on map click
  const handleViewOnMap = () => {
    if (alert?.sensor_id) {
      navigate('/map', { state: { highlightSensor: alert.sensor_id } });
    }
  };
  
  // Handle acknowledge dialog open
  const handleAcknowledgeOpen = () => {
    setAcknowledgeDialogOpen(true);
  };
  
  // Handle acknowledge dialog close
  const handleAcknowledgeClose = () => {
    setAcknowledgeDialogOpen(false);
  };
  
  // Handle acknowledge alert
  const handleAcknowledgeAlert = async () => {
    try {
      if (!id) return;
      
      await apiClient.post('/alerts/acknowledge', {
        alert_id: parseInt(id),
        notes: notes.trim() || undefined
      });
      
      setAcknowledgeDialogOpen(false);
      fetchAlert(); // Refresh data
      
    } catch (err: any) {
      console.error('Error acknowledging alert:', err);
      setError(
        formatErrorMessage(err) || 
        t('alertDetail.errors.failedToAcknowledge')
      );
    }
  };

  // Handle delete alert
  const handleDeleteAlert = async () => {    
    try {
      if (!id) return;

      setDeleteAlertStatus('loading');

      // Use direct fetch with correct API URL to handle 204 No Content response
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/alerts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Check if response is ok without attempting to parse the body
      if (response.ok) {
        setDeleteAlertStatus('success');
        setAlertMessage(t('sensor.deleteAcknowledgedAlertsSuccess') as string || 'Acknowledged alerts deleted successfully');
        
        // Close dialog
        setDeleteAlertDialogOpen(false);
        
        // Return to alerts list
        navigate('/alerts', { state: { snackbar: { message: t('sensor.deleteAcknowledgedAlertsSuccess') as string || 'Acknowledged alerts deleted successfully', severity: 'success' } } });
        
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
    } catch (err: any) {
      console.error('Error deleting alert:', err);
      console.error('Error deleting acknowledged alert:', err);
      setDeleteAlertStatus('error');
      setAlertMessage(t('sensor.deleteAcknowledgedAlertError') as string || 'Failed to delete acknowledged alert');
    }
  };

  // Handle open delete alerts dialog
  const handleOpenDeleteAlertDialog = () => {
    setDeleteAlertDialogOpen(true);
  };

  // Handle close delete alerts dialog
  const handleCloseDeleteAlertsDialog = () => {
    setDeleteAlertDialogOpen(false);
  };

  // Format event data for display
  const formatEventData = (data: Record<string, any> | undefined) => {
    if (!data) return null;
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('alertDetail.eventData')}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <pre style={{ margin: 0, overflow: 'auto' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Paper>
      </Box>
    );
  };

  if (loading && !alert) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{t('alertDetail.title')}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {alert && !alert.is_acknowledged && (
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={handleAcknowledgeOpen}
            color="primary"
          >
            {t('alertDetail.acknowledgeAlert')}
          </Button>
        )}
        {alert && alert.is_acknowledged && user?.is_admin && (
          <Tooltip title="Delete">
            <IconButton
              onClick={handleOpenDeleteAlertDialog}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <MuiAlert
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchAlert}>
              {t('alertDetail.retry')}
            </Button>
          }
        >
          {error}
        </MuiAlert>
      )}

      {alert && (
        <Grid container spacing={3}>
          {/* Alert Overview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h5">
                    {t(`eventTypes.${alert.event_type}`, t('eventTypes.default'))}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {alert.is_acknowledged ? (
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label={t('alertDetail.acknowledged')}
                      color="success"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorOutlineIcon />}
                      label={t('alertDetail.unacknowledged')}
                      color="warning"
                    />
                  )}
                </Box>
                <Typography variant="subtitle1" color="text.secondary">
                  {formatLocalDateTime(alert.created_at)}
                </Typography>
                {alert.alert_level !== null && alert.alert_level !== undefined && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t('alerts.alertLevel')} :
                    </Typography>
                    <Chip
                      label={`${t('alerts.level')} ${alert.alert_level}`}
                      color={alert.alert_level >= 3 ? 'error' : alert.alert_level >= 2 ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SensorsIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="subtitle1">
                    {t('alertDetail.sensorInformation')}
                  </Typography>
                </Box>
                
                <Card variant="outlined" sx={{ mt: 1 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {alert.sensor_name}
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={handleViewSensor}
                        startIcon={<SensorsIcon fontSize="small" />}
                      >
                        {t('alertDetail.viewSensor')}
                      </Button>
                    </Box>
                    
                    {alert.sensor_serial_number && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('sensor.fields.serialNumber')}: {alert.sensor_serial_number}
                      </Typography>
                    )}
                    
                    {alert.sensor_imei && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        IMEI: {alert.sensor_imei}
                      </Typography>
                    )}
                    
                    {alert.sensor_location_description && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {alert.sensor_location_description}
                        </Typography>
                      </Box>
                    )}
                    
                    {alert.sensor_location && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={handleViewOnMap}
                        >
                          {t('alertDetail.viewOnMap')}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
              
              {alert.is_acknowledged && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="subtitle1">
                      {t('alertDetail.acknowledgement')}
                    </Typography>
                  </Box>
                  
                  <Card variant="outlined" sx={{ mt: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">
                          {t('alertDetail.acknowledgedBy')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {alert.acknowledger_name || t('alertDetail.unknownUser')}
                        </Typography>
                      </Box>
                      
                      {alert.acknowledged_at && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="body2">
                            {t('alertDetail.acknowledgedAt')}
                          </Typography>
                          <Typography variant="body2">
                            {formatLocalDateTime(alert.acknowledged_at)}
                          </Typography>
                        </Box>
                      )}
                      
                      {alert.notes && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('alertDetail.notes')}
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, backgroundColor: '#f5f5f5' }}>
                            <Typography variant="body2">
                                {(() => {
                                  let displayNote = '';
                                  try {
                                    const parsed = JSON.parse(alert.notes || '');
                                    if (parsed.system_note_key) {
                                      displayNote = `${t(parsed.system_note_key)}${parsed.user_note ? ' - ' + parsed.user_note : ''}`;
                                    } else {
                                      displayNote = alert.notes;
                                    }
                                  } catch {
                                    displayNote = alert.notes;
                                  }
                                  return displayNote;
                                })()}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}
              
              {formatEventData(alert.event_data)}
            </Paper>
          </Grid>

          {/* TODO: Alert Visualization - Section temporairement masquée */}
          {/*
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotesIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('alertDetail.visualization.title')}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70%' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('alertDetail.visualization.notAvailable')}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          */}
        </Grid>
      )}

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialogOpen} onClose={handleAcknowledgeClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('alertDetail.dialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('alertDetail.dialog.acknowledging', { id, type: t(`eventTypes.${alert?.event_type}`, t('eventTypes.default')) })} 
          </Typography>
          <Typography variant="body2" paragraph>
            {t('alertDetail.dialog.explanation')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('alertDetail.dialog.notesLabel')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('alertDetail.dialog.notesPlaceholder') as string}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAcknowledgeClose}>{t('alertDetail.dialog.cancel')}</Button>
          <Button onClick={handleAcknowledgeAlert} variant="contained" color="primary">
            {t('alertDetail.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Acknowledged Alerts Dialog */}
      <Dialog open={deleteAlertDialogOpen} onClose={handleCloseDeleteAlertsDialog}>
        <DialogTitle>{t('alerts.deleteAcknowledgedAlerts')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('alerts.deleteAcknowledgedAlertsConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteAlertsDialog}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAlert} 
            color="error" 
            variant="contained"
            disabled={deleteAlertStatus === 'loading'}
          >
            {deleteAlertStatus === 'loading' ? <CircularProgress size={24} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={deleteAlertStatus === 'success' || deleteAlertStatus === 'error'}
        autoHideDuration={6000}
        onClose={() => setDeleteAlertStatus('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setDeleteAlertStatus('idle')} 
          severity={deleteAlertStatus === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertDetail;